import {
  createContext,
  useContext,
  type ReactNode,
  useMemo,
  useState,
  type SetStateAction,
  type Dispatch,
} from 'react'
import { useNavigate, useParams } from 'router-v6'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { usePhoneStore } from 'v2/state/phone'
import {
  getAgentSip,
  getOutgoingCallDirection,
  parseConversationURL,
} from 'v2/pages/conversations/utils'
import { CallDirection } from 'v2/types/call'
import { Views } from 'v2/types'
import { useHoldCallMutation, useToggleRecordingMutation } from '../../mutations/call'

interface CallContextType {
  isHoldCallPending: boolean
  isToggleRecordingPending: boolean
  isIncomingCallRinging: boolean
  showConversationButton: boolean
  showTransferModal: boolean
  handleToggleHold: () => void
  handleNavigateToConversation: () => void
  handleToggleRecording: () => void
  handleShowTransferModal: Dispatch<SetStateAction<boolean>>
}

const CallContext = createContext<CallContextType | undefined>(undefined)

export const useCallContext = () => {
  const context = useContext(CallContext)
  if (!context) throw new Error('useCallContext must be used within CallProvider')
  return context
}

interface CallProviderProps {
  children: ReactNode
}

export const CallProvider = ({ children }: CallProviderProps) => {
  const { activeCall, isCallActive, isOnHold, callConversation } = usePhoneStore()
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const { viewId } = parseConversationURL()
  const holdCall = useHoldCallMutation()
  const toggleRecordingMutation = useToggleRecordingMutation()
  const [showTransferModal, setShowTransferModal] = useState(false)

  const { callChildConversation, parentConversation } = callConversation || {}

  const isIncomingCallRinging = !isCallActive && activeCall?.direction !== CallDirection.OUTBOUND
  const showConversationButton =
    (!!parentConversation && conversationId !== parentConversation?.conversation_number) ||
    viewId !== Views.active

  const handleToggleHold = () => {
    if (!parentConversation?.conversation_number || !callChildConversation?.contact_uuid) return

    holdCall.mutate({
      payload: {
        direction: getOutgoingCallDirection(activeCall?.meta, activeCall?.direction),
        agent_sip_endpoint: `sip:${phoneSDK.getUsername()}@phone.plivo.com`,
        hold: !isOnHold,
        conversation_number: parentConversation.conversation_number,
      },
      callUUID: callChildConversation.contact_uuid,
    })
  }

  const handleToggleRecording = () => {
    if (!callChildConversation?.contact_uuid) return

    toggleRecordingMutation.mutate({
      callUUID: callChildConversation.contact_uuid,
      payload: {
        call_direction: getOutgoingCallDirection(activeCall?.meta, activeCall?.direction),
        agent_sip_endpoint: getAgentSip(),
        pauseRecording: !usePhoneStore.getState().isRecordingPaused,
      },
    })
  }

  const handleNavigateToConversation = () => {
    if (!parentConversation?.conversation_number) return
    navigate(`/conversations/active/c/${parentConversation.conversation_number}`)
  }

  const value = useMemo(
    () => ({
      isToggleRecordingPending: toggleRecordingMutation.isPending,
      isHoldCallPending: holdCall.isPending,
      isIncomingCallRinging,
      showConversationButton,
      showTransferModal,
      handleToggleHold,
      handleNavigateToConversation,
      handleShowTransferModal: setShowTransferModal,
      handleToggleRecording,
    }),
    [
      holdCall.isPending,
      toggleRecordingMutation.isPending,
      isIncomingCallRinging,
      showConversationButton,
      callChildConversation,
      parentConversation,
      showTransferModal,
    ],
  )

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>
}
